import { initializeApp, getApp, getApps, FirebaseApp, FirebaseOptions } from "firebase/app";
import { getMessaging, isSupported as isMessagingSupported, Messaging } from "firebase/messaging";
import { Analytics, getAnalytics, isSupported as isAnalyticsSupported } from "firebase/analytics";

/**
 * Firebase web configuration, sourced from environment variables so that each
 * environment (dev / staging / prod) can point at its own Firebase project.
 *
 * These values are safe to ship to the browser — Firebase treats the web config
 * (including the API key) as public — but we still keep them in `.env` so they
 * are not hard-coded per environment. See `.env.example` for the full list.
 */
export const firebaseConfig: FirebaseOptions = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

export const firebaseVapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY ?? "";

export const isFirebaseConfigured = (): boolean =>
  Boolean(
    firebaseConfig.apiKey &&
      firebaseConfig.projectId &&
      firebaseConfig.messagingSenderId &&
      firebaseConfig.appId,
  );

let firebaseApp: FirebaseApp | null = null;


export const getFirebaseApp = (): FirebaseApp => {
  if (!isFirebaseConfigured()) {
    throw new Error(
      "Firebase is not configured. Set the REACT_APP_FIREBASE_* variables in your .env file.",
    );
  }

  if (firebaseApp) return firebaseApp;

  firebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);
  return firebaseApp;
};

let messagingPromise: Promise<Messaging | null> | null = null;


export const getFirebaseMessaging = async (): Promise<Messaging | null> => {
  if (!messagingPromise) {
    messagingPromise = (async () => {
      try {
        if (!isFirebaseConfigured()) return null;
        if (!(await isMessagingSupported())) {
          console.warn("[firebase] Cloud Messaging is not supported in this browser.");
          return null;
        }
        return getMessaging(getFirebaseApp());
      } catch (error) {
        console.error("[firebase] Failed to initialize Cloud Messaging:", error);
        return null;
      }
    })();
  }

  return messagingPromise;
};

let analyticsPromise: Promise<Analytics | null> | null = null;


export const getFirebaseAnalytics = async (): Promise<Analytics | null> => {
  if (!analyticsPromise) {
    analyticsPromise = (async () => {
      try {
        if (!isFirebaseConfigured() || !firebaseConfig.measurementId) return null;
        if (!(await isAnalyticsSupported())) return null;
        return getAnalytics(getFirebaseApp());
      } catch (error) {
        console.error("[firebase] Failed to initialize Analytics:", error);
        return null;
      }
    })();
  }

  return analyticsPromise;
};
